import type { CashierCurrencyType } from "@workspace/casino_core/schema";
import useOpenCloseCashier from "@workspace/casino_pages/legacyLayer/client/components/CashierWrap/useCashier/useOpenCloseCashier";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import getCashierLink from "@/client/components/CashierWrap/useCashier/getCashierLink/getCashierLink";
import useCashierTheme from "@/client/components/CashierWrap/useCashier/useCashierTheme";
import CommsCommands from "@/client/helpers/CommsCommands";
import ProtoCommands from "@/client/helpers/ProtoCommands";
import useOnClickOutside from "@/client/hooks/useOnClickOutside";

type Props = {
  isMobile: boolean;
  cashierPath?: string;
  type: CashierCurrencyType;
  name: CashierCurrency;
  currentTheme?: ColorScheme;
  isProtoEnabled: boolean;
  site: Site;
};

const useCashier = ({
  cashierPath = "home",
  site,
  type,
  name,
  isMobile,
  currentTheme,
  isProtoEnabled,
}: Props) => {
  const { isOpen, handleOpen, handleClose, setOpenStore } = useOpenCloseCashier(
    { cashierPath, type, name },
  );
  const pathname = usePathname() ?? "";
  const params = useSearchParams();
  const isCashierOpen = params?.get("c");
  const link = getCashierLink(pathname, cashierPath);

  useEffect(() => {
    window.addEventListener("openCashier", handleOpen);
    return () => {
      window.removeEventListener("openCashier", handleOpen);
    };
  }, []);

  useEffect(() => {
    const protoEL = document.getElementById("sinitic-webchat-wrapper");

    if (isCashierOpen) {
      setOpenStore();
      protoEL?.style.setProperty("z-index", "10001");
    } else {
      protoEL?.style.setProperty("z-index", "49");
    }
  }, [setOpenStore, isCashierOpen]);

  const { elementRef } = useOnClickOutside({
    handler: () => {
      if (!isMobile) {
        handleClose();
      }
    },
    allowCommsClick: true,
  });

  useCashierTheme({ site, setClose: handleClose, isOpen, currentTheme });

  if (global.Cashier) {
    global.Cashier.OpenComms = () => {
      if (isProtoEnabled) {
        global.Cashier?.Close?.();
        ProtoCommands.open();
      } else {
        CommsCommands.show({});
      }
    };
  }

  return { isOpen, link, handleOpen, handleClose, elementRef };
};

export default useCashier;
